import { atom, Getter, Setter } from 'jotai';
import { atomFamily, atomWithStorage, createJSONStorage } from 'jotai/utils';
import { TZDate } from '@date-fns/tz';

import { TimeSpan, TrendChart, TrendGroup } from '@/features/trends/trends.types';
import { mapTimeSpanToDateRange } from '@/features/trends/trends.utils';
import { createFacilityDate } from '@/helpers/datetime/facility-date';
import CurrentFacilityAtoms from '@/state/current-facility.state';
import { timezoneAtom } from '@/state/time.state';
import { generateUUID } from '@/utils/uuid';

import { currentChartIdAtom } from './current-chart.state';

const LOCALSTORAGE_DEBOUNCE_MS = 500; // Debounce duration for localStorage writes

const filterGroupsForStorage = (groups: TrendGroup[]) =>
  groups.map((group) => {
    if (group.timeSpan === 'date-range') return group;
    const { start, end, ...rest } = group;
    return rest;
  });

const syncChartsCacheFromGroups = (get: Getter, set: Setter, groups: TrendGroup[]) => {
  const allCharts: Record<string, TrendChart> = {};
  for (const group of groups) {
    for (const chart of group.charts) {
      allCharts[chart.id] = chart;
    }
  }
  set(chartsCache, allCharts);

  const currentChartId = get(currentChartIdAtom);
  if (!currentChartId || !(currentChartId in allCharts)) {
    set(currentChartIdAtom, groups[0]?.charts[0]?.id ?? null);
  }
};

/**
 * HYBRID STATE ARCHITECTURE
 *
 * Combines the best of both approaches:
 * 1. Keep localStorage simple (1 entry per agent, like current)
 * 2. Use atomFamily for isolated in-memory chart atoms (no cascading re-renders)
 * 3. Debounce localStorage writes to reduce write frequency
 * 4. Pagination offsets in sessionStorage (survives reload; cleared when the tab closes;
 *    not mixed with chart config in localStorage)
 *
 * Benefits:
 * - Fast, isolated updates in memory (no re-renders)
 * - Simple localStorage structure (1 entry)
 * - Efficient persistence (batched writes)
 * - No migration needed (same format as before)
 * - Pagination offsets stay out of trend-groups localStorage
 */

// ============================================================================
// PAGINATION OFFSETS (sessionStorage, per agent)
// ============================================================================

const TREND_PAGINATION_OFFSETS_SESSION_KEY = 'trend-pagination-offsets';

type GroupPaginationOffsets = Record<string, { start: Date; end: Date }>;
type PaginationOffsetsByAgent = Record<string, GroupPaginationOffsets>;

// Dates serialize to ISO strings via Date.prototype.toJSON; the reviver restores them on read.
// Only the innermost `start`/`end` keys hold dates in this shape, so the key check is safe.
const paginationOffsetsSessionStorage = createJSONStorage<PaginationOffsetsByAgent>(
  () => globalThis.sessionStorage,
  {
    reviver: (key, value) =>
      (key === 'start' || key === 'end') && typeof value === 'string'
        ? createFacilityDate(value)
        : value,
  }
);

const paginationOffsetsByAgentSessionAtom = atomWithStorage<PaginationOffsetsByAgent>(
  TREND_PAGINATION_OFFSETS_SESSION_KEY,
  {},
  paginationOffsetsSessionStorage
);
paginationOffsetsByAgentSessionAtom.debugLabel = 'paginationOffsetsByAgentSessionAtom';

type SetPaginationOffsetsAction =
  | GroupPaginationOffsets
  | ((prev: GroupPaginationOffsets) => GroupPaginationOffsets);

/**
 * Pagination offsets from prev/next time navigation, keyed by trend group id for the
 * current agent. Persisted to sessionStorage so a full page reload keeps the panned window
 * until the tab is closed. Scoped by agent id under the hood.
 */
export const paginationOffsetsAtom = atom(
  (get): GroupPaginationOffsets => {
    const agentId = get(CurrentFacilityAtoms.agentId);
    if (!agentId) return {};
    return get(paginationOffsetsByAgentSessionAtom)[agentId] ?? {};
  },
  (get, set, update: SetPaginationOffsetsAction) => {
    const agentId = get(CurrentFacilityAtoms.agentId);
    if (!agentId) return;

    const all = get(paginationOffsetsByAgentSessionAtom);
    const prev = all[agentId] ?? {};
    const next = typeof update === 'function' ? update(prev) : update;

    const nextAll: PaginationOffsetsByAgent = { ...all };
    if (Object.keys(next).length === 0) {
      delete nextAll[agentId];
    } else {
      nextAll[agentId] = next;
    }
    set(paginationOffsetsByAgentSessionAtom, nextAll);
  }
);
paginationOffsetsAtom.debugLabel = 'paginationOffsetsAtom';

// ============================================================================
// IN-MEMORY CHART ATOMS (Isolated, Fast)
// ============================================================================

/**
 * In-memory cache of all charts, keyed by chartId.
 * This is the source of truth during runtime.
 */
const chartsCache = atom<Record<string, TrendChart>>({});
chartsCache.debugLabel = 'chartsCache';

/**
 * Internal cache atom that stores trend groups per agent ID.
 * This maintains the same localStorage structure as before.
 */
const trendGroupsCache = atom<Record<string, TrendGroup[]>>({});
trendGroupsCache.debugLabel = 'trendGroupsCache';

/**
 * Atom family for individual charts.
 * These are in-memory only and provide isolated updates.
 *
 * Benefits:
 * - Updating chart A doesn't re-render chart B
 * - No immediate localStorage writes
 * - Stable atom references (atomFamily handles memoization)
 *
 * IMPORTANT: No need to useMemo - atomFamily handles it automatically
 *
 * Usage:
 * ```typescript
 * const [chart, setChart] = useAtom(chartAtomFamily(chartId));
 * ```
 */
export const chartAtomFamily = atomFamily((chartId: string) => {
  const chartAtom = atom(
    (get) => {
      const cache = get(chartsCache);
      return cache[chartId] ?? null;
    },
    (get, set, updated: TrendChart) => {
      const cache = get(chartsCache);
      // Only update this specific chart in the cache
      set(chartsCache, { ...cache, [chartId]: updated });

      // Trigger debounced save
      set(debouncedSaveAtom);
    }
  );
  chartAtom.debugLabel = `chartAtomFamily(${chartId})`;
  return chartAtom;
});

// ============================================================================
// TREND GROUPS (Nested Structure in localStorage, Flat in Memory)
// ============================================================================

/**
 * Atom storing the array of trend groups for the current agent.
 * This maintains backwards compatibility with existing localStorage format.
 *
 * On load: Populates chartsCache from localStorage
 * On save: Writes entire structure to localStorage (same as before)
 */
export const trendGroupsAtom = atom(
  (get) => {
    const agentId = get(CurrentFacilityAtoms.agentId);
    const cache = get(trendGroupsCache);
    const charts = get(chartsCache);

    if (!agentId) return [];

    // Return cached data for this agent if available
    if (cache[agentId]?.length > 0) {
      // IMPORTANT: Hydrate with fresh chart data from chartsCache
      return cache[agentId].map((group) => {
        const chartsHydrated = group.charts.map((chart) => charts[chart.id] || chart);
        return { ...group, charts: chartsHydrated };
      });
    }

    // Load from localStorage for this agent
    const storedItem = localStorage.getItem('trend-groups-' + agentId);
    const groups: TrendGroup[] = storedItem ? JSON.parse(storedItem) : [];

    // Deserialize Date objects from ISO strings and compute dates for non-date-range timeSpans
    const hydratedGroups = groups.map((group) => {
      if (group.timeSpan === 'date-range') {
        // For date-range, use persisted dates
        return {
          ...group,
          start: createFacilityDate(group.start),
          end: group.end ? createFacilityDate(group.end) : undefined,
        };
      }

      // For non-date-range timeSpans, compute dates dynamically (ignore persisted dates)
      const dateRange = mapTimeSpanToDateRange(group.timeSpan);
      return {
        ...group,
        start: createFacilityDate(dateRange.from),
        end: dateRange.to ? createFacilityDate(dateRange.to) : undefined,
      };
    });

    // Return groups - initialization will populate cache
    return hydratedGroups;
  },
  (get, set, updated: TrendGroup[]) => {
    const agentId = get(CurrentFacilityAtoms.agentId);
    if (!agentId) return;

    // Save to localStorage (strip start/end from non-date-range groups)
    localStorage.setItem(
      'trend-groups-' + agentId,
      JSON.stringify(filterGroupsForStorage(updated))
    );

    // Update cache for this specific agent (keep full groups in cache for runtime)
    const cache = get(trendGroupsCache);
    set(trendGroupsCache, { ...cache, [agentId]: updated });

    syncChartsCacheFromGroups(get, set, updated);
  }
);
trendGroupsAtom.debugLabel = 'trendGroupsAtom';

/**
 * Derived atom that returns the ID of the first chart in the first group.
 * This is used to set the current chart ID on mount.
 */
export const firstChartIdAtom = atom((get) => {
  const trendGroups = get(trendGroupsAtom);
  return trendGroups?.[0]?.charts?.[0]?.id ?? null;
});
firstChartIdAtom.debugLabel = 'firstChartIdAtom';

/**
 * Initialization atom - call this once on app load to populate chartsCache
 * and create default group if none exists
 */
export const initializeTrendGroupsAtom = atom(null, (get, set) => {
  const agentId = get(CurrentFacilityAtoms.agentId);
  if (!agentId) return;

  let groups = get(trendGroupsAtom);

  // If no groups exist, create a default group with sensible defaults
  if (groups.length === 0) {
    const defaultChart: TrendChart = {
      id: generateUUID(),
      name: 'Chart 1',
      selection: {
        showAdvanced: false,
        searchTerm: '',
        expandedSections: [],
        attributes: [],
      },
      isExpanded: true,
      displayMode: 'actual',
    };

    // Compute dates dynamically for the default timeSpan (1hr)
    const defaultTimeSpan: TimeSpan = '1hr';
    const dateRange = mapTimeSpanToDateRange(defaultTimeSpan);

    groups = [
      {
        id: generateUUID(),
        name: 'Trend Group 1',
        interval: 'per-minute',
        timeSpan: defaultTimeSpan,
        start: createFacilityDate(dateRange.from),
        end: dateRange.to ? createFacilityDate(dateRange.to) : undefined,
        charts: [defaultChart],
        isExpanded: true,
      },
    ];

    // Save the default group using the setter (which handles localStorage and cache)
    // The setter will filter out start/end for non-date-range timeSpans when saving
    set(trendGroupsAtom, groups);
  }

  // Update cache
  const cache = get(trendGroupsCache);
  set(trendGroupsCache, { ...cache, [agentId]: groups });

  syncChartsCacheFromGroups(get, set, groups);
});

// ============================================================================
// DEBOUNCED PERSISTENCE
// ============================================================================

let saveTimeout: ReturnType<typeof setTimeout> | null = null;

/**
 * Atom that triggers a debounced save to localStorage.
 * Batches multiple rapid updates into a single write.
 */
const debouncedSaveAtom = atom(null, (get, set) => {
  // Clear existing timeout
  if (saveTimeout) {
    clearTimeout(saveTimeout);
  }

  // Debounce for LOCALSTORAGE_DEBOUNCE_MS
  saveTimeout = setTimeout(() => {
    const agentId = get(CurrentFacilityAtoms.agentId);
    if (!agentId) return;

    // Get current state
    const currentGroups = get(trendGroupsCache)[agentId];
    if (!currentGroups) return;

    const charts = get(chartsCache);

    // Reconstruct groups with updated charts from cache
    const updatedGroups = currentGroups.map((group) => ({
      ...group,
      charts: group.charts.map((chart) => charts[chart.id] || chart),
    }));

    // Save to localStorage (strip start/end from non-date-range groups)
    localStorage.setItem(
      'trend-groups-' + agentId,
      JSON.stringify(filterGroupsForStorage(updatedGroups))
    );

    // Update trendGroupsCache to keep it in sync (keep full groups in cache for runtime)
    const cache = get(trendGroupsCache);
    set(trendGroupsCache, { ...cache, [agentId]: updatedGroups });
  }, LOCALSTORAGE_DEBOUNCE_MS);
});

// ============================================================================
// EXISTING ATOMS (Backward Compatible)
// ============================================================================

/**
 * Creates a derived atom for accessing and updating a specific trend group by ID.
 * Returns null if the trend group is not found.
 *
 * IMPORTANT:  MUST BE USED WITH useMemo if used inside the react render loop because
 * it's a function that returns an atom which is not a stable reference
 */
export const trendGroupAtom = (groupId: string) =>
  atom(
    (get) => {
      const trendGroups = get(trendGroupsAtom);
      return trendGroups.find((group: TrendGroup) => group.id === groupId) ?? null;
    },
    (get, set, updated: TrendGroup) => {
      const trendGroups = get(trendGroupsAtom);
      set(
        trendGroupsAtom,
        trendGroups.map((group: TrendGroup) => (group.id === updated.id ? updated : group))
      );
    }
  );

/**
 * Creates a derived atom that returns the effective date range for a trend group,
 * combining the persisted dates with session-only pagination offsets.
 *
 * Use this atom when you need to read the current date range that a chart should display.
 * This will return paginated dates if the user has clicked prev/next, otherwise the
 * original dates from the persisted group.
 *
 * IMPORTANT: MUST BE USED WITH useMemo if used inside the react render loop
 */
export const trendGroupWithPaginationAtom = (groupId: string) =>
  atom((get) => {
    const group = get(trendGroupAtom(groupId));
    if (!group) return null;

    const paginationOffsets = get(paginationOffsetsAtom);
    const offset = paginationOffsets[groupId];

    // If there's a pagination offset, use it; otherwise use the group's dates
    if (offset) {
      return {
        ...group,
        start: offset.start,
        end: offset.end,
      };
    }

    return group;
  });

// ============================================================================
// CANONICAL QUERY WINDOW RESOLVER
// ============================================================================

export type QueryWindowReason =
  | 'preset-live'
  | 'date-range-today-live'
  | 'historical'
  | 'paginated-history';

export interface ResolvedQueryWindow {
  start: Date;
  end: Date | undefined;
  isLive: boolean;
  reason: QueryWindowReason;
}

/**
 * Returns true when `date` falls on the current calendar day in `timezone`.
 */
function isDateToday(date: Date, timezone: string): boolean {
  const tzDate = new TZDate(date, timezone);
  const tzNow = new TZDate(new Date(), timezone);
  return (
    tzDate.getFullYear() === tzNow.getFullYear() &&
    tzDate.getMonth() === tzNow.getMonth() &&
    tzDate.getDate() === tzNow.getDate()
  );
}

/**
 * Canonical resolver for the effective query time window of a trend group.
 *
 * Encodes the single source of truth for live vs. historical:
 * - Preset timespan with no pagination -> live
 * - date-range ending "today" in facility timezone -> live
 * - date-range ending before today -> historical
 * - Any pagination offset -> historical
 *
 * Product note (custom date range + "today"): when the user picks today, `group.end` reflects
 * selection-time semantics (effectively "now" at selection). If that end falls on the current
 * calendar day in the facility timezone, the query stays **live** (open-ended) until the
 * facility-local date is no longer "today" (after midnight), including near end-of-day—do not
 * treat an earlier same-day `group.end` instant as a reason to force historical mode.
 *
 * IMPORTANT: MUST BE USED WITH useMemo if used inside the react render loop
 */
export const resolvedQueryWindowAtom = (groupId: string) =>
  atom<ResolvedQueryWindow | null>((get) => {
    const group = get(trendGroupAtom(groupId));
    if (!group) return null;

    const paginationOffsets = get(paginationOffsetsAtom);
    const offset = paginationOffsets[groupId];
    const timezone = get(timezoneAtom);

    if (offset) {
      return {
        start: offset.start,
        end: offset.end,
        isLive: false,
        reason: 'paginated-history',
      };
    }

    if (group.timeSpan !== 'date-range') {
      const dateRange = mapTimeSpanToDateRange(group.timeSpan);
      return {
        start: createFacilityDate(dateRange.from),
        end: undefined,
        isLive: true,
        reason: 'preset-live',
      };
    }

    // date-range: check if end is "today" in facility timezone
    if (group.end && isDateToday(group.end, timezone)) {
      return {
        start: group.start,
        end: undefined,
        isLive: true,
        reason: 'date-range-today-live',
      };
    }

    return {
      start: group.start,
      end: group.end,
      isLive: false,
      reason: 'historical',
    };
  });

/**
 * Derived atom that automatically resolves to the trend group containing the current chart.
 * Returns null if no chart is currently selected or if the chart isn't found in any group.
 */
export const currentTrendGroupAtom = atom<TrendGroup | null>((get) => {
  const trendGroups: TrendGroup[] = get(trendGroupsAtom);
  const currentChartId = get(currentChartIdAtom);

  return (
    trendGroups.find((group) => group.charts.some((chart) => chart.id === currentChartId)) ?? null
  );
});
currentTrendGroupAtom.debugLabel = 'currentTrendGroupAtom';

/**
 * Creates a derived atom that finds the trend group containing a specific chart.
 * Returns null if the chart is not found in any group.
 *
 * IMPORTANT:  MUST BE USED WITH useMemo if used inside the react render loop
 */
export const trendGroupByChartIdAtom = (chartId: string) =>
  atom<TrendGroup | null>((get) => {
    const trendGroups: TrendGroup[] = get(trendGroupsAtom);
    return (
      trendGroups.find((group: TrendGroup) =>
        group.charts.some((chart: TrendChart) => chart.id === chartId)
      ) ?? null
    );
  });
